/**
 * Product mix roll-ups for the Products page.
 *
 * The API returns cells of period × kind × plan, each already carrying its
 * molecule and supply length (backend services/productMix decides those, so
 * the page never re-parses a plan id). Everything here is regrouping: pick a
 * path of dimensions, and every level of the tree, every period and every
 * share is a sum over the same cells. So a row always adds up to its children,
 * and the shares in a column add up to 100.
 */

import { PRODUCT_SERIES } from './velocity'
import { getBrandColor } from './brandColors'

/** How the table drills: the top level first. */
export const GROUPINGS = [
  { key: 'molecule', label: 'Molecule → Supply', path: ['molecule', 'supply', 'plan'] },
  { key: 'supply', label: 'Supply → Molecule', path: ['supply', 'molecule', 'plan'] },
  { key: 'plan', label: 'Plan', path: ['plan'] },
]

/** Only when every clinic is shown. */
export const CLINIC_GROUPING = { key: 'clinic', label: 'Clinic → Molecule', path: ['clinic', 'molecule', 'supply'] }

export const DIMENSION_LABELS = { molecule: 'Molecule', supply: 'Supply', plan: 'Plan', clinic: 'Clinic' }

const MOLECULE_COLORS = {
  // The Velocity chart's colours, so a molecule looks the same on every page.
  ...Object.fromEntries(PRODUCT_SERIES.map(s => [s.label, s.color])),
  Phentermine: '#0EA5E9',
  Orforglipron: '#10B981',
  'GLP-1 (unspecified)': '#C084FC',
  'NAD+': '#EC4899',
  'Hair Loss': '#84CC16',
  Testosterone: '#EF4444',
  B12: '#14B8A6',
}

/** Shortest supply lightest: one hue, so length reads as an amount. */
const SUPPLY_SHADES = ['#BFDBFE', '#93C5FD', '#60A5FA', '#3B82F6', '#2563EB', '#1D4ED8', '#1E40AF', '#1E3A8A']

const PALETTE = ['#6366F1', '#F59E0B', '#10B981', '#EC4899', '#0EA5E9', '#8B5CF6', '#EF4444', '#84CC16', '#14B8A6', '#F97316']

const OTHER_COLOR = '#94A3B8'

/** Months in a supply label, for ordering; Unknown last. */
export function supplyMonthsOf(label) {
  const n = parseInt(label, 10)
  return Number.isFinite(n) ? n : Infinity
}

function keyOf(cell, dimension) {
  if (dimension === 'plan') return cell.entity_id
  return cell[dimension] ?? 'Unknown'
}

function labelOf(cell, dimension) {
  if (dimension === 'plan') return cell.name || cell.entity_id
  return keyOf(cell, dimension)
}

const empty = () => ({ count: 0, revenue: 0, cogs: 0 })

function add(into, cell) {
  into.count += cell.count
  into.revenue += cell.revenue
  into.cogs += cell.cogs
}

/**
 * Cells → a tree along `path`.
 *
 * Each node: { id, key, label, dimension, total, byPeriod, children }.
 * Siblings are ordered by the measure, except supply, which orders by length.
 *
 * @param {object[]} cells
 * @param {string[]} path     dimensions, top level first
 * @param {string} measure    'count' | 'revenue' — the sibling order
 */
export function buildTree(cells, path, measure = 'count') {
  const root = { id: 'root', total: empty(), byPeriod: {}, children: new Map() }
  for (const cell of cells) {
    add(root.total, cell)
    add((root.byPeriod[cell.period] ||= empty()), cell)
    let node = root
    path.forEach((dimension, depth) => {
      const key = keyOf(cell, dimension)
      let child = node.children.get(key)
      if (!child) {
        child = {
          id: `${node.id}/${key}`,
          key,
          label: labelOf(cell, dimension),
          dimension,
          depth,
          total: empty(),
          byPeriod: {},
          children: new Map(),
        }
        node.children.set(key, child)
      }
      add(child.total, cell)
      add((child.byPeriod[cell.period] ||= empty()), cell)
      node = child
    })
  }

  const finish = node => {
    const kids = [...node.children.values()].map(finish)
    kids.sort((a, b) => (a.dimension === 'supply'
      ? supplyMonthsOf(a.key) - supplyMonthsOf(b.key)
      : b.total[measure] - a.total[measure]))
    return { ...node, children: kids }
  }
  return finish(root)
}

/** A colour per top-level group, stable for a given dimension. */
export function colorFor(dimension, key, index = 0, allKeys = []) {
  if (dimension === 'molecule') return MOLECULE_COLORS[key] || (key === 'Other' ? OTHER_COLOR : PALETTE[index % PALETTE.length])
  if (dimension === 'clinic') return getBrandColor(key)
  if (dimension === 'supply') {
    if (key === 'Unknown') return OTHER_COLOR
    const ordered = allKeys.filter(k => k !== 'Unknown').sort((a, b) => supplyMonthsOf(a) - supplyMonthsOf(b))
    const at = Math.max(0, ordered.indexOf(key))
    // Spread across the shades so two lengths are never neighbours in colour.
    const step = ordered.length > 1 ? (SUPPLY_SHADES.length - 1) / (ordered.length - 1) : 0
    return SUPPLY_SHADES[Math.round(at * step)]
  }
  return PALETTE[index % PALETTE.length]
}

/**
 * Chart rows: one per period, one key per top-level group (the top `limit`
 * by the measure, the rest summed as "Other"). Values are the measure, or the
 * group's share of the period when `share` is set. Revenue is in dollars.
 */
export function chartSeries(tree, periods, { measure = 'count', share = false, limit = 8 } = {}) {
  const top = tree.children.slice(0, limit)
  const rest = tree.children.slice(limit)
  const allKeys = tree.children.map(g => g.key)
  const series = top.map((group, i) => ({
    key: `g${i}`,
    label: group.label,
    color: colorFor(group.dimension, group.key, i, allKeys),
    node: group,
  }))
  if (rest.length) series.push({ key: 'rest', label: `${rest.length} more`, color: OTHER_COLOR, nodes: rest })

  const scale = measure === 'revenue' ? 1 / 100 : 1
  const rows = periods.map(period => {
    const periodTotal = tree.byPeriod[period]?.[measure] || 0
    const row = { period }
    for (const s of series) {
      const raw = s.node
        ? s.node.byPeriod[period]?.[measure] || 0
        : s.nodes.reduce((sum, n) => sum + (n.byPeriod[period]?.[measure] || 0), 0)
      row[s.key] = share ? (periodTotal ? (100 * raw) / periodTotal : 0) : raw * scale
    }
    return row
  })
  return { series, rows }
}

/** Derived figures for one node, against its parent's total for the shares. */
export function figures(node, of) {
  const t = node.total
  return {
    count: t.count,
    countShare: of?.count ? t.count / of.count : null,
    revenue: t.revenue,
    revenueShare: of?.revenue ? t.revenue / of.revenue : null,
    aov: t.count ? t.revenue / t.count : null,
    cogs: t.cogs,
    margin: t.revenue - t.cogs,
    marginRate: t.revenue ? (t.revenue - t.cogs) / t.revenue : null,
  }
}

/** "2026-10-05" → "Oct 5" / "Wk Oct 5"; "2026-10" → "Oct 2026". */
export function periodLabel(period, granularity) {
  if (/^\d{4}-\d{2}$/.test(period)) {
    const [y, m] = period.split('-').map(Number)
    return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })
  }
  const day = new Date(`${period}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
  return granularity === 'weekly' ? `Wk ${day}` : day
}
